import { Link } from "react-router-dom";
import type { Product } from "@/types";
import { formatBRL, installmentText } from "@/lib/format";
import { useStore } from "@/store/StoreContext";

interface ProductGridCardProps {
  product: Product;
}

export default function ProductGridCard({ product }: ProductGridCardProps) {
  const { isFavorite, toggleFavorite } = useStore();
  const favorite = isFavorite(product.id);
  const hasPromo = typeof product.promoPrice === "number";
  const price = product.promoPrice ?? product.price;
  const secondImage = product.colors[1]?.image;

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50">
      <div className="relative h-[340px] w-full overflow-hidden bg-background-100 md:h-[380px]">
        <Link to={`/produto/${product.id}`} className="block h-full w-full cursor-pointer">
          <img
            src={product.colors[0].image}
            alt={`${product.name} — ${product.category} Amora Belle`}
            title={`${product.name} — ${product.category} moda feminina`}
            className={`h-full w-full object-cover object-top transition-all duration-700 ease-out group-hover:scale-[1.06] ${
              secondImage ? "group-hover:opacity-0" : ""
            }`}
          />
          {secondImage ? (
            <img
              src={secondImage}
              alt={`${product.name} em outra cor — Amora Belle`}
              title={`${product.name} — variação de cor Amora Belle`}
              className="absolute inset-0 h-full w-full object-cover object-top opacity-0 transition-opacity duration-700 ease-out group-hover:opacity-100"
            />
          ) : null}
        </Link>

        {product.tag ? (
          <span
            className={`absolute left-3 top-3 rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-[0.14em] ${
              product.tag === "Novo"
                ? "bg-secondary-500 text-foreground-950"
                : product.tag === "Mais vendido"
                  ? "bg-primary-600 text-background-50"
                  : "bg-accent-500 text-background-50"
            }`}
          >
            {product.tag}
          </span>
        ) : null}

        <button
          type="button"
          onClick={() => toggleFavorite(product)}
          aria-label={favorite ? `Remover ${product.name} dos favoritos` : `Favoritar ${product.name}`}
          aria-pressed={favorite}
          className={`absolute right-3 top-3 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full backdrop-blur-sm transition-colors ${
            favorite
              ? "bg-primary-500 text-background-50"
              : "bg-background-50/85 text-foreground-700 hover:bg-background-50"
          }`}
        >
          <i className={favorite ? "ri-heart-3-fill text-lg" : "ri-heart-3-line text-lg"} />
        </button>
      </div>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] uppercase tracking-[0.18em] text-secondary-700">
            {product.category}
          </span>
          <span className="text-[11px] tracking-[0.1em] text-foreground-400">{product.code}</span>
        </div>
        <h3 className="mt-1.5 font-heading text-xl leading-snug text-foreground-900">
          <Link to={`/produto/${product.id}`} className="cursor-pointer transition-colors hover:text-primary-600">
            {product.name}
          </Link>
        </h3>

        <div className="mt-3 flex items-end justify-between gap-3">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-base font-medium text-primary-700">{formatBRL(price)}</span>
              {hasPromo ? (
                <span className="text-xs text-foreground-400 line-through">
                  {formatBRL(product.price)}
                </span>
              ) : null}
            </div>
            <p className="text-[11px] text-foreground-500">
              {installmentText(price, product.installments)}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            {product.colors.slice(0, 3).map((color) => (
              <span
                key={color.name}
                title={color.name}
                className="h-4 w-4 rounded-full border border-background-300"
                style={{ backgroundColor: color.hex }}
              />
            ))}
          </div>
        </div>

        <Link
          to={`/produto/${product.id}`}
          className="mt-4 inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
        >
          Ver detalhes
          <i className="ri-arrow-right-line text-base" />
        </Link>
      </div>
    </article>
  );
}